<template>
    <div
        class="relative w-full cursor-pointer select-none rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
        :class="{ 'flashcard-card--flipped': isFlipped }"
        role="button"
        tabindex="0"
        :aria-pressed="String(isFlipped)"
        :aria-label="localizations[lang].flashcard.flipCard"
        @click="$emit('flip')"
        @keydown.enter="$emit('flip')"
        @keydown.space.prevent="$emit('flip')"
    >
        <div class="flashcard-card-inner grid w-full" :class="sizeClasses.minHeight">
            <div
                class="flashcard-card-face flashcard-card-front flex flex-col items-center justify-center rounded-xl border-2 p-6 [grid-area:1/1]"
                :style="faceStyle"
            >
                <span
                    class="absolute left-4 top-3 text-xs font-semibold uppercase tracking-wide text-theme-text opacity-60"
                >
                    {{ localizations[lang].flashcard.front }}
                </span>
                <p
                    class="w-full text-center leading-relaxed text-theme-text [overflow-wrap:anywhere]"
                    :class="sizeClasses.text"
                >
                    {{ card.frontText }}
                </p>
            </div>
            <div
                class="flashcard-card-face flashcard-card-back flex flex-col items-center justify-center rounded-xl border-2 p-6 [grid-area:1/1]"
                :style="backFaceStyle"
            >
                <span
                    class="absolute left-4 top-3 text-xs font-semibold uppercase tracking-wide text-theme-text opacity-60"
                >
                    {{ localizations[lang].flashcard.back }}
                </span>
                <p
                    class="w-full text-center leading-relaxed text-theme-text [overflow-wrap:anywhere]"
                    :class="sizeClasses.text"
                >
                    {{ card.backText }}
                </p>
            </div>
        </div>
        <span class="mt-2 block text-center text-xs text-theme-toolbar-icon opacity-50">
            {{ localizations[lang].flashcard.flipCard }}
        </span>
    </div>
</template>

<script>
'use strict';

import { mapGetters, mapState } from 'vuex';

export default {
    name: 'FlashcardCard',
    props: {
        card: { type: Object, required: true },
        isFlipped: { type: Boolean, default: false },
        size: {
            type: String,
            default: 'default',
            validator: value => ['default', 'fullscreen'].includes(value),
        },
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['allHighlighters']),
        /**
         * Tailwind classes that scale the card height and text in the fullscreen study view.
         * @returns {{ minHeight: string, text: string }}
         */
        sizeClasses() {
            return this.size === 'fullscreen'
                ? { minHeight: 'min-h-[340px]', text: 'text-xl' }
                : { minHeight: 'min-h-[200px]', text: 'text-base' };
        },
        /**
         * @returns {string} Hex color for the card border, resolved from its color slug
         */
        cardColorHex() {
            const matchedColor = this.allHighlighters.find(color => color.slug === this.card.colorSlug);
            return matchedColor ? matchedColor.color : this.card.color;
        },
        /**
         * @returns {Object} Inline border color shared by both faces (front keeps the neutral theme surface)
         */
        faceStyle() {
            return { borderColor: `${this.cardColorHex}66` };
        },
        /**
         * Inline styles for the back (reverso) face only: the shared border plus a faint wash of the
         * card's own color over the theme surface, so only the reverso reads as color-filled while
         * the front keeps its plain theme background.
         * @returns {Object} Inline style object for the back face
         */
        backFaceStyle() {
            return {
                ...this.faceStyle,
                backgroundImage: `linear-gradient(${this.cardColorHex}1f, ${this.cardColorHex}1f)`,
            };
        },
    },
};
</script>

<style>
.flashcard-card-inner {
    perspective: 800px;
}

.flashcard-card-face {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    background-color: var(--theme-background-card);
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.flashcard-card-front {
    transform: rotateY(0deg);
}

.flashcard-card-back {
    transform: rotateY(180deg);
}

.flashcard-card--flipped .flashcard-card-front {
    transform: rotateY(180deg);
}

.flashcard-card--flipped .flashcard-card-back {
    transform: rotateY(360deg);
}
</style>
